import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Shield, Truck, RefreshCcw } from "lucide-react";
import { api } from "@/lib/api";

const SEED_REVIEWS = [
    {
        id: "seed-1",
        name: "Marcus T.",
        verified: true,
        rating: 5,
        title: "Built different.",
        body: "The hoodie hits different. Heavier than anything I own and the print stays sharp wash after wash. Already grabbed a second.",
        product_name: "Monolith Hoodie",
    },
    {
        id: "seed-2",
        name: "Jade R.",
        verified: true,
        rating: 5,
        title: "Worth every dollar.",
        body: "Tried it expecting hype. Got craftsmanship. Fits true, weighs heavy in the hand. This is the kind of piece you keep for years.",
        product_name: "Rage Wolf Tee",
    },
    {
        id: "seed-3",
        name: "Devon K.",
        verified: true,
        rating: 5,
        title: "Energy matched. Literally.",
        body: "Three compliments first time wearing it. The fit is exact. Shipping was faster than expected. Will be back for the next drop.",
        product_name: "Primate Oversized Tee",
    },
];

const TRUST = [
    { icon: Truck, label: "Flat $11.95 shipping · every order" },
    { icon: RefreshCcw, label: "30-day returns, no questions" },
    { icon: Shield, label: "Authenticity guaranteed" },
];

const shorten = (name) => name; // already short

const buyersCount = (real) =>
    // Bump a base count by real review count so it grows over time
    (2847 + real).toLocaleString();

const avgRating = (reviews) => {
    if (!reviews.length) return "4.9";
    const sum = reviews.reduce((s, r) => s + r.rating, 0);
    return (sum / reviews.length).toFixed(1);
};

export default function Worn() {
    const [reviews, setReviews] = useState(SEED_REVIEWS);
    const [realCount, setRealCount] = useState(0);

    useEffect(() => {
        let mounted = true;
        api.get("/reviews", { params: { limit: 12 } })
            .then((res) => {
                if (!mounted) return;
                const data = res.data || [];
                setRealCount(data.length);
                if (data.length > 0) {
                    // Mix real reviews first, then top up with seed if < 3
                    const combined = [...data];
                    for (const s of SEED_REVIEWS) {
                        if (combined.length >= 3) break;
                        combined.push(s);
                    }
                    setReviews(combined.slice(0, 3));
                }
            })
            .catch(() => {});
        return () => {
            mounted = false;
        };
    }, []);

    return (
        <section
            id="worn"
            data-testid="worn-section"
            className="relative bg-[#070707] py-24 lg:py-32 border-t border-white/10 overflow-hidden"
        >
            <p
                aria-hidden
                className="absolute -top-10 right-4 lg:right-12 font-display text-[14rem] lg:text-[22rem] leading-none text-outline opacity-[0.10] select-none pointer-events-none tabular-nums"
            >
                03
            </p>

            <div className="relative max-w-[1600px] mx-auto px-6 lg:px-12">
                <div className="grid grid-cols-12 gap-6 mb-16 items-end">
                    <div className="col-span-12 md:col-span-7">
                        <p className="label-mono mb-4">— 03 / Worn By You</p>
                        <h2 className="font-display text-5xl md:text-7xl lg:text-8xl uppercase tracking-tighter leading-[0.85]">
                            Real energy.
                            <br />
                            <span className="italic font-normal">Real reviews.</span>
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-5 md:text-right">
                        <div className="flex md:justify-end items-center gap-2 mb-2">
                            {[1, 2, 3, 4, 5].map((i) => (
                                <Star
                                    key={i}
                                    size={20}
                                    fill="currentColor"
                                    className="text-white"
                                />
                            ))}
                        </div>
                        <p className="font-display text-3xl md:text-4xl">
                            {avgRating(reviews)} / 5.0
                        </p>
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-2">
                            From {buyersCount(realCount)} verified buyers
                        </p>
                    </div>
                </div>

                <div
                    className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8"
                    data-testid="worn-reviews"
                >
                    {reviews.map((r, i) => (
                        <motion.article
                            key={r.id || r.name + i}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{
                                duration: 0.8,
                                delay: i * 0.1,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            className="border border-white/10 p-8 bg-black/40 backdrop-blur-sm hover:border-white/30 transition-colors flex flex-col"
                            data-testid={`review-card-${i}`}
                        >
                            <div className="flex items-center gap-1 mb-4">
                                {[1, 2, 3, 4, 5].map((s) => (
                                    <Star
                                        key={s}
                                        size={12}
                                        fill="currentColor"
                                        className={
                                            s <= r.rating
                                                ? "text-white"
                                                : "text-white/20"
                                        }
                                    />
                                ))}
                            </div>
                            <p className="font-display text-2xl md:text-3xl leading-tight">
                                "{r.title}"
                            </p>
                            <p className="font-sans text-base text-muted-foreground mt-4 leading-relaxed flex-1">
                                {r.body}
                            </p>
                            <div className="border-t border-white/10 mt-6 pt-4 flex items-center justify-between">
                                <div>
                                    <p className="font-mono text-xs uppercase tracking-[0.2em]">
                                        {shorten(r.name)}
                                    </p>
                                    {r.verified && (
                                        <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-muted-foreground mt-1 flex items-center gap-1">
                                            <Shield size={9} />
                                            Verified Buyer
                                        </p>
                                    )}
                                </div>
                                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground text-right max-w-[120px]">
                                    {r.product_name || ""}
                                </p>
                            </div>
                        </motion.article>
                    ))}
                </div>

                <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 border-t border-white/15 pt-10">
                    {TRUST.map((t, i) => (
                        <motion.div
                            key={t.label}
                            initial={{ opacity: 0, y: 15 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: i * 0.1 }}
                            className="flex items-center gap-4"
                            data-testid={`trust-${i}`}
                        >
                            <t.icon size={20} className="text-white shrink-0" />
                            <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/80">
                                {t.label}
                            </p>
                        </motion.div>
                    ))}
                </div>

                <div className="mt-16 text-center">
                    <p className="label-mono mb-3">Ready to match the energy?</p>
                    <a
                        href="#shop"
                        data-testid="worn-cta-shop"
                        data-cursor-label="Shop ↓"
                        className="inline-flex items-center gap-3 bg-white text-black px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] hover:bg-white/85 transition-colors"
                    >
                        Shop the drop
                        <span>→</span>
                    </a>
                </div>
            </div>
        </section>
    );
}
